import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { formatIsoDay, parseBatchExpiry } from '@wodi/domain';
import { ADJUSTMENT_REASONS, type StockItemView } from '@wodi/shared';
import { Button, Notice, PageHeader, RadioGroup, Select, TextArea, TextField } from '@wodi/ui';
import { useState } from 'react';

import { createAdjustment, storeItemQuery } from '../api/stock.ts';
import { useOnline } from '../hooks/use-online.ts';
import { formatIsoDate } from '../patients/format.ts';
import { BackToStore, errorText, newRequestKey, WithStore } from '../stock/common.tsx';
import { formatQuantity, readQuantity, REASON_LABELS } from '../stock/format.ts';
import { StockItemPicker } from '../stock/item-picker.tsx';
import { screen } from '../layout/screen.tsx';
import styles from '../stock/stock.module.css';
import { toasts } from '../toasts.ts';

const route = getRouteApi('/_app/stores/$storeId/adjust');

/**
 * Request an adjustment or an opening balance (CLAUDE.md, domain rule 10). Nothing moves
 * until someone else with approval rights approves it.
 */
export function RequestAdjustmentPage() {
  const { storeId } = route.useParams();
  return (
    <WithStore storeId={storeId}>
      {(store) => <AdjustmentForm storeId={store.id} storeName={store.name} />}
    </WithStore>
  );
}

function AdjustmentForm({ storeId, storeName }: { storeId: string; storeName: string }) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<'adjustment' | 'opening_balance'>('adjustment');
  const [reason, setReason] = useState<string | null>(null);
  const [direction, setDirection] = useState<'remove' | 'add'>('remove');
  const [item, setItem] = useState<StockItemView | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [newBatch, setNewBatch] = useState('');
  const [expiryText, setExpiryText] = useState('');
  const [quantityText, setQuantityText] = useState('');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [requestKey] = useState(newRequestKey);
  const held = useQuery({
    ...storeItemQuery(storeId, item?.itemId ?? ''),
    enabled: item !== null,
  });

  const adding = kind === 'opening_balance' || direction === 'add';
  const useNewBatch = adding && batchId === null;
  const quantity = readQuantity(quantityText);
  const expiry = parseBatchExpiry(expiryText);
  const needsNote = reason === 'other' || reason === 'theft_suspected';
  const expiryOk = item?.stock?.expiryTracked === false || expiry.ok;
  const valid =
    item !== null &&
    (quantity ?? 0) > 0 &&
    (kind === 'opening_balance' || reason !== null) &&
    (!needsNote || note.trim() !== '') &&
    (useNewBatch ? newBatch.trim() !== '' && expiryOk : batchId !== null);

  const submit = useMutation({
    mutationFn: () =>
      createAdjustment({
        clientRequestId: requestKey,
        storeId,
        kind,
        ...(kind === 'adjustment' && reason !== null
          ? { reasonCode: reason as (typeof ADJUSTMENT_REASONS)[number] }
          : {}),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        occurredAt: new Date().toISOString(),
        lines: [
          {
            itemId: item?.itemId ?? '',
            batch: useNewBatch
              ? {
                  newBatch: {
                    batchNumber: newBatch.trim(),
                    expiryDate: expiry.ok ? formatIsoDay(expiry.value.day) : null,
                  },
                }
              : { batchId: batchId ?? '' },
            quantity: adding ? (quantity ?? 0) : -(quantity ?? 0),
          },
        ],
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['stock', storeId] });
      toasts.show({ title: 'Adjustment requested', description: result.number });
      void navigate({ to: '/stores', search: { storeId } });
    },
  });

  const unit = item?.stock?.baseUnit.display ?? '';
  return (
    <div className={styles.page}>
      <BackToStore storeId={storeId} name={storeName} />
      <PageHeader title={`Request an adjustment in ${storeName}`} />
      <p>Another person with approval rights must approve it before stock changes.</p>
      <form
        className={`${styles.wideForm} ${screen.panel}`}
        onSubmit={(e) => {
          e.preventDefault();
          setTried(true);
          if (valid) submit.mutate();
        }}
      >
        <RadioGroup
          label="What is this?"
          options={[
            { id: 'adjustment', label: 'A correction to stock' },
            { id: 'opening_balance', label: 'An opening balance at go-live' },
          ]}
          value={kind}
          onChange={(v) => {
            setKind(v as 'adjustment' | 'opening_balance');
          }}
        />
        {kind === 'adjustment' ? (
          <>
            <Select
              label="Reason"
              placeholder="Choose a reason"
              options={ADJUSTMENT_REASONS.map((r) => ({ id: r, label: REASON_LABELS[r] }))}
              value={reason}
              onChange={(v) => {
                setReason(String(v));
                if (v === 'found') setDirection('add');
                if (v === 'expired' || v === 'damaged' || v === 'lost' || v === 'theft_suspected')
                  setDirection('remove');
              }}
              {...(tried && reason === null ? { errorMessage: 'Choose a reason.' } : {})}
            />
            <RadioGroup
              label="Stock to"
              options={[
                { id: 'remove', label: 'Remove' },
                { id: 'add', label: 'Add' },
              ]}
              value={direction}
              onChange={(v) => {
                setDirection(v as 'remove' | 'add');
              }}
            />
          </>
        ) : null}
        <StockItemPicker
          onSelect={(chosen) => {
            setItem(chosen);
            setBatchId(null);
          }}
        />
        {item === null ? null : (
          <>
            <p className={styles.lineHeading}>{item.name}</p>
            {held.isSuccess && held.data.batches.length > 0 ? (
              <Select
                label="Batch"
                placeholder={adding ? 'A new batch' : 'Choose a batch'}
                options={[
                  ...held.data.batches.map((b) => ({
                    id: b.batchId,
                    label: `${b.batchNumber}${b.expiryDate === null ? '' : `, expires ${formatIsoDate(b.expiryDate)}`}: ${formatQuantity(b.quantity, unit)}`,
                  })),
                ]}
                value={batchId}
                onChange={(v) => {
                  setBatchId(String(v));
                }}
                {...(tried && !adding && batchId === null
                  ? { errorMessage: 'Choose the batch.' }
                  : {})}
              />
            ) : !adding ? (
              <Notice tone="info">
                This store holds none of this item, so there is nothing to remove.
              </Notice>
            ) : null}
            {useNewBatch ? (
              <div className={styles.row}>
                <TextField
                  label="Batch number"
                  value={newBatch}
                  onChange={setNewBatch}
                  {...(tried && newBatch.trim() === ''
                    ? { errorMessage: 'Enter the batch number.' }
                    : {})}
                />
                {item.stock?.expiryTracked === false ? null : (
                  <TextField
                    label="Expiry"
                    description="As printed: 03/2027"
                    value={expiryText}
                    onChange={setExpiryText}
                    {...(tried && !expiry.ok
                      ? { errorMessage: 'Enter the expiry as on the pack.' }
                      : {})}
                  />
                )}
              </div>
            ) : null}
            <TextField
              label="Quantity"
              unit={unit}
              inputMode="numeric"
              value={quantityText}
              onChange={setQuantityText}
              {...(tried && (quantity ?? 0) === 0
                ? { errorMessage: 'Enter a whole number above zero.' }
                : {})}
            />
          </>
        )}
        <TextArea
          label={needsNote ? 'What happened' : 'Note (optional)'}
          value={note}
          onChange={setNote}
          {...(tried && needsNote && note.trim() === ''
            ? { errorMessage: 'Say what happened.' }
            : {})}
        />
        {submit.error === null ? null : (
          <Notice tone="error">
            {errorText(submit.error, "The request wasn't saved. Try again.")}
          </Notice>
        )}
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={submit.isPending}
            pendingLabel="Sending…"
          >
            Request approval
          </Button>
        </div>
      </form>
    </div>
  );
}
